import { Check, Copy } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { LiksIconButton } from '../../components/liks/LiksIconButton/LiksIconButton';
import { LiksTooltip } from '../../components/liks/LiksTooltip/LiksTooltip';

const COPY_FEEDBACK_MS = 1500;

type KnowledgeDocumentUrlCellProps = {
  url: string;
  copyLabel: string;
  copiedLabel: string;
};

export function KnowledgeDocumentUrlCell({
  url,
  copyLabel,
  copiedLabel,
}: KnowledgeDocumentUrlCellProps) {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);

      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }

      copyTimeoutRef.current = window.setTimeout(() => {
        setCopied(false);
        copyTimeoutRef.current = null;
      }, COPY_FEEDBACK_MS);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="knowledge-database__url-cell-content">
      <LiksTooltip
        content={copied ? copiedLabel : copyLabel}
        placement="top-start"
      >
        <LiksIconButton
          type="button"
          className={[
            'knowledge-database__url-copy',
            copied && 'knowledge-database__url-copy--copied',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-label={copied ? copiedLabel : copyLabel}
          onClick={() => {
            void handleCopy();
          }}
        >
          {copied ? (
            <Check strokeWidth={2} aria-hidden />
          ) : (
            <Copy strokeWidth={2} aria-hidden />
          )}
        </LiksIconButton>
      </LiksTooltip>
      <LiksTooltip
        className="knowledge-database__url-tooltip"
        content={url}
        placement="top-start"
        block
      >
        <a
          className="knowledge-database__source-url"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          {url}
        </a>
      </LiksTooltip>
    </div>
  );
}
